import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./style.scss";

// ツールチップの固定幅
const DEFAULT_TOOLTIP_WIDTH = 280;
// ボタンとツールチップの間隔
const TOOLTIP_OFFSET = 8;
// ツールチップを表示する際のレイアウトの境界を指定するセレクタ（ツールチップがここをはみ出ないようにする）
const REFERENCE_TOOLTIP_BOUNDARY_SELECTOR = "[data-reference-tooltip-boundary]";

const getTooltipLayout = (triggerElement: HTMLSpanElement | null) => {
  if (!triggerElement) return null;

  const boundary = triggerElement.closest(REFERENCE_TOOLTIP_BOUNDARY_SELECTOR);

  if (!(boundary instanceof HTMLElement)) {
    return null;
  }

  return {
    portalTarget: boundary,
    rect: boundary.getBoundingClientRect(),
    position: "absolute" as const
  };
};

// ツールチップの位置を計算する
const getTooltipPosition = (
  triggerRect: DOMRect,
  layoutRect: Pick<DOMRect, "left" | "right" | "top" | "bottom">,
  bubbleHeight: number
) => {
  const centeredLeft = triggerRect.left + triggerRect.width / 2;
  const minLeft = layoutRect.left + TOOLTIP_OFFSET;
  const maxLeft = layoutRect.right - DEFAULT_TOOLTIP_WIDTH - TOOLTIP_OFFSET;
  const left =
    maxLeft < minLeft
      ? layoutRect.left +
        (layoutRect.right - layoutRect.left - DEFAULT_TOOLTIP_WIDTH) / 2
      : Math.min(
          Math.max(centeredLeft - DEFAULT_TOOLTIP_WIDTH / 2, minLeft),
          maxLeft
        );

  const topAbove = triggerRect.top - bubbleHeight - TOOLTIP_OFFSET;
  const topBelow = triggerRect.bottom + TOOLTIP_OFFSET;
  const minTop = layoutRect.top + TOOLTIP_OFFSET;
  const maxTop = layoutRect.bottom - TOOLTIP_OFFSET - bubbleHeight;
  const nextTop = topAbove >= minTop ? topAbove : topBelow;

  return {
    left,
    top: Math.max(minTop, Math.min(nextTop, maxTop))
  };
};

interface Props {
  children: React.ReactNode;
  content?: () => React.ReactNode;
  className?: string;
}

export const ReferenceTooltip: React.FC<Props> = ({
  children,
  content,
  className
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
    position: "absolute" | "fixed";
  } | null>(null);

  const resolvedContent = content ? content() : null;
  const canShow = resolvedContent != null;
  const show = isOpen && canShow;

  useEffect(() => {
    if (!isOpen || !canShow) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const triggerElement = triggerRef.current;
      const layout = getTooltipLayout(triggerElement);
      if (!triggerElement || !layout) return;

      const nextPosition = getTooltipPosition(
        triggerElement.getBoundingClientRect(),
        layout.rect,
        bubbleRef.current?.offsetHeight ?? 0
      );

      setPosition({
        left:
          layout.position === "absolute"
            ? nextPosition.left - layout.rect.left
            : nextPosition.left,
        top:
          layout.position === "absolute"
            ? nextPosition.top - layout.rect.top
            : nextPosition.top,
        position: layout.position
      });
    };

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [canShow, isOpen]);

  const portalTarget = show
    ? getTooltipLayout(triggerRef.current)?.portalTarget
    : null;

  const portal = portalTarget
    ? createPortal(
        <span
          ref={bubbleRef}
          className={styles.referenceTooltipBubble}
          aria-hidden
          style={{
            left: position?.left ?? -9999,
            top: position?.top ?? -9999,
            width: DEFAULT_TOOLTIP_WIDTH,
            position: position?.position ?? "fixed",
            visibility: position ? "visible" : "hidden"
          }}
        >
          {resolvedContent}
        </span>,
        portalTarget
      )
    : null;

  return (
    <span
      ref={triggerRef}
      className={
        className
          ? `${styles.referenceTooltip} ${className}`
          : styles.referenceTooltip
      }
      onMouseEnter={() => {
        setIsOpen(true);
      }}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => setIsOpen(true)}
      onBlur={() => setIsOpen(false)}
    >
      {children}
      {portal}
    </span>
  );
};
